import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import API from "../../api/api";
import PageSectionsRenderer from "../../components/common/PageSectionsRenderer";

const formatDate = (raw) => {
  if (!raw) return "Date TBD";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
};

const formatEventDateRange = (startRaw, endRaw) => {
  const start = startRaw || null;
  const end = endRaw || startRaw || null;
  if (!start && !end) return "Date TBD";
  if (!start) return formatDate(end);
  if (!end || end === start) return formatDate(start);
  return `${formatDate(start)} to ${formatDate(end)}`;
};

const keyOf = (value) => String(value || "").trim().toLowerCase();

const getGameIcon = (gameType) => {
  const type = String(gameType || "").toUpperCase();
  if (type === "TIMED") return "⏱️";
  if (type === "DISTANCE" || type === "DISTANCE_HEIGHT") return "📏";
  if (type === "MAX_WEIGHT") return "🏋️";
  if (type === "BRACKET") return "🎾";
  return "🎯";
};

const getGameBorderClass = (gameType) => {
  const type = String(gameType || "").toUpperCase();
  if (type === "TIMED") return "border-blue-200";
  if (type === "DISTANCE" || type === "DISTANCE_HEIGHT") return "border-emerald-200";
  if (type === "MAX_WEIGHT") return "border-purple-200";
  if (type === "BRACKET") return "border-fuchsia-200";
  return "border-slate-200";
};

const getGameBadgeClass = (gameType) => {
  const type = String(gameType || "").toUpperCase();
  if (type === "TIMED") return "bg-blue-50/80 border-blue-100";
  if (type === "DISTANCE" || type === "DISTANCE_HEIGHT") return "bg-emerald-50/80 border-emerald-100";
  if (type === "MAX_WEIGHT") return "bg-purple-50/80 border-purple-100";
  if (type === "BRACKET") return "bg-fuchsia-50/80 border-fuchsia-100";
  return "bg-slate-50 border-slate-100";
};

const getScoreBubbleClass = (gameType) => {
  const type = String(gameType || "").toUpperCase();
  if (type === "TIMED") return "bg-blue-50 text-blue-800 border-blue-100";
  if (type === "DISTANCE" || type === "DISTANCE_HEIGHT") return "bg-emerald-50 text-emerald-800 border-emerald-100";
  if (type === "MAX_WEIGHT") return "bg-purple-50 text-purple-800 border-purple-100";
  if (type === "BRACKET") return "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-100";
  return "bg-slate-50 text-slate-800 border-slate-100";
};

const formatResultValue = (row) => {
  const type = String(row.game_type || "").toUpperCase();
  if (type === "TIMED") {
    const timeVal = row.result_time || "";
    const msVal = row.result_time_ms !== null && row.result_time_ms !== undefined ? String(row.result_time_ms).padStart(2, "0") : "";
    if (timeVal) {
      return msVal ? `${timeVal}.${msVal} s` : `${timeVal} s`;
    }
    return "No time";
  }
  if (type === "DISTANCE" || type === "DISTANCE_HEIGHT") {
    return row.points_score !== null && row.points_score !== undefined
      ? `${row.points_score.toFixed(2)} m`
      : "No score";
  }
  if (type === "MAX_WEIGHT") {
    return row.points_score !== null && row.points_score !== undefined
      ? `${row.points_score.toFixed(1)} kg`
      : "No weight";
  }
  if (type === "POINTS" || type === "TEAM_SPORT") {
    return row.points_score !== null && row.points_score !== undefined
      ? `${row.points_score} pts`
      : "No score";
  }
  if (type === "BRACKET") {
    return "Winner";
  }
  return row.points_score !== null && row.points_score !== undefined
    ? String(row.points_score)
    : "No score";
};

const renderRankBadge = (pos) => {
  if (pos === 1) return <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 font-extrabold text-[11px] text-amber-700 border border-amber-200 shadow-sm" title="Gold Medalist">1st</span>;
  if (pos === 2) return <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 font-extrabold text-[11px] text-slate-700 border border-slate-200 shadow-sm" title="Silver Medalist">2nd</span>;
  if (pos === 3) return <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-orange-100 font-extrabold text-[11px] text-orange-700 border border-orange-200 shadow-sm" title="Bronze Medalist">3rd</span>;
  if (pos) return <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-50 font-bold text-[10px] text-blue-600 border border-blue-100" title={`Rank ${pos}`}>{pos}</span>;
  return <span className="text-slate-400 text-xs font-semibold">-</span>;
};

const UpdatesResults = () => {
  const [sections, setSections] = useState([]);
  const [liveFeed, setLiveFeed] = useState({ enabled: true, pause_message: "", items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(60);

  const refreshResults = useCallback(async () => {
    try {
      const liveRes = await API.fetchCurrentLiveResults();
      setLiveFeed({
        enabled: liveRes?.enabled !== false,
        pause_message: liveRes?.pause_message || "",
        items: Array.isArray(liveRes?.items) ? liveRes.items : [],
      });
    } catch (_err) {
      // Silent refresh failure; keep previous live feed.
    }
  }, []);

  // Always keep ref to latest refreshResults to avoid stale closures in timer
  const refreshRef = useRef(refreshResults);
  useEffect(() => {
    refreshRef.current = refreshResults;
  }, [refreshResults]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [sectionRes, liveRes] = await Promise.all([
          API.fetchAboutSections("updates_results"),
          API.fetchCurrentLiveResults(),
        ]);
        if (!mounted) return;
        setSections(Array.isArray(sectionRes) ? sectionRes : []);
        setLiveFeed({
          enabled: liveRes?.enabled !== false,
          pause_message: liveRes?.pause_message || "",
          items: Array.isArray(liveRes?.items) ? liveRes.items : [],
        });
      } catch (err) {
        if (!mounted) return;
        setError(err?.message || "Failed to load updates and results.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  // Poll timer countdown and refresh every 1 minute (no stale closure)
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          refreshRef.current();
          return 60;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const sectionMap = useMemo(() => {
    const map = {};
    (Array.isArray(sections) ? sections : [])
      .filter((s) => s?.is_active !== false)
      .forEach((s) => {
        map[keyOf(s.section_key)] = s;
      });
    return map;
  }, [sections]);

  const pageMeta = sectionMap["updates_results_meta"] || null;

  const liveEvents = useMemo(() => {
    const grouped = new Map();
    (Array.isArray(liveFeed?.items) ? liveFeed.items : []).forEach((row) => {
      const id = String(row?.event_id || "");
      if (!id) return;
      if (!grouped.has(id)) {
        grouped.set(id, {
          event_id: row.event_id,
          event_title: row.event_title || "Live Event",
          event_date: row.event_date || null,
          event_end_date: row.event_end_date || null,
          location: row.location || "Location TBD",
          latest_updated_at: row.updated_at || null,
          rows: [],
        });
      }
      const bucket = grouped.get(id);
      bucket.rows.push(row);
      if (row?.updated_at && (!bucket.latest_updated_at || new Date(row.updated_at) > new Date(bucket.latest_updated_at))) {
        bucket.latest_updated_at = row.updated_at;
      }
    });

    return Array.from(grouped.values())
      .map((group) => ({
        ...group,
        rows: group.rows
          .slice()
          .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)),
      }))
      .sort((a, b) => new Date(b.latest_updated_at || 0) - new Date(a.latest_updated_at || 0));
  }, [liveFeed]);



  return (
    <div className="bg-slate-50 min-h-screen">
      <Navbar />

      <section className="bg-white border-b border-slate-200 py-14 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900">
            {pageMeta?.heading || "Updates & Results"}
          </h1>
          <p className="text-lg text-slate-600 mt-3">
            {pageMeta?.subheading || "Track live updates and ongoing championships results."}
          </p>
        </div>
      </section>

      <main className="max-w-screen-xl mx-auto px-6 py-10 space-y-10">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
            <p className="text-slate-500 font-semibold text-sm">Loading ongoing results...</p>
          </div>
        ) : null}
        
        {error ? (
          <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-center text-red-600 font-semibold shadow-sm">
            ⚠️ {error}
          </div>
        ) : null}

        {!loading && !error ? (
          <div className="space-y-8">
            
            {/* Live Progress Bar Header */}
            {liveFeed?.enabled && liveEvents.length > 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 overflow-hidden relative">
                {/* Horizontal shrinking countdown bar */}
                <div 
                  className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-1000"
                  style={{ width: `${(countdown / 60) * 100}%` }}
                />
                
                <div className="flex items-center gap-3">
                  <div className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Recent & Live Results</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Events from the last 7 days — auto-updates every minute</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2.5 text-xs text-slate-600 font-semibold bg-slate-50 border border-slate-200/60 rounded-xl px-4 py-2">
                  <span>⏱️ Auto-updates in: <strong className="text-blue-600">{countdown}s</strong></span>
                </div>
              </div>
            ) : null}

            {/* Results Grid */}
            {!liveFeed?.enabled ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-12 text-center text-amber-800 shadow-sm">
                <span className="text-3xl block mb-2">⏸️</span>
                <p className="font-bold text-lg">{liveFeed?.pause_message || "Live scoreboard is temporarily paused."}</p>
                <p className="text-sm text-slate-600 mt-1">Please refresh later to check ongoing standings.</p>
              </div>
            ) : liveEvents.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-16 text-center text-slate-500 shadow-sm flex flex-col items-center justify-center gap-3">
                <span className="text-4xl">🏆</span>
                <h3 className="font-bold text-lg text-slate-800">No Recent Event Results</h3>
                <p className="text-sm text-slate-500 max-w-md">No results found from the last 7 days. Check back once events are entered and results are recorded.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-8">
                {liveEvents.map((event) => {
                  // Group rows by game
                  const gamesMap = {};
                  event.rows.forEach((row) => {
                    const key = row.game_id || row.game_name || "unknown";
                    if (!gamesMap[key]) {
                      gamesMap[key] = {
                        game_name: row.game_name || "Unknown Game",
                        game_type: row.game_type || "POINTS",
                        results: [],
                      };
                    }
                    gamesMap[key].results.push(row);
                  });

                  // Sort results by rank (result_position)
                  Object.values(gamesMap).forEach((g) => {
                    g.results.sort((a, b) => {
                      const aPos = a.result_position !== null ? a.result_position : 999;
                      const bPos = b.result_position !== null ? b.result_position : 999;
                      return aPos - bPos;
                    });
                  });

                  return (
                    <article key={event.event_id} className="bg-white border border-slate-200/80 rounded-2xl shadow-sm overflow-hidden flex flex-col">
                      {/* Event Banner Header */}
                      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-6 py-5 text-white flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          {(() => {
                            const todayStr = new Date().toISOString().slice(0, 10);
                            const endDate = event.event_end_date || event.event_date || "";
                            const isLive = endDate >= todayStr;
                            return isLive ? (
                              <span className="inline-flex items-center gap-1 bg-red-500 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full mb-1">
                                <span className="relative flex h-1.5 w-1.5">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white"></span>
                                </span>
                                LIVE TODAY
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 bg-slate-600 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full mb-1">
                                ✓ RECENT RESULTS
                              </span>
                            );
                          })()}
                          <h3 className="font-extrabold text-xl tracking-tight leading-tight">{event.event_title}</h3>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-300 text-xs mt-2 font-semibold">
                            <span className="flex items-center gap-1">📅 {formatEventDateRange(event.event_date, event.event_end_date)}</span>
                            <span className="flex items-center gap-1">📍 {event.location || "Location TBD"}</span>
                          </div>
                        </div>
                      </div>

                      {/* Games stand list */}
                      <div className="p-6 space-y-8">
                        {Object.values(gamesMap).map((game, gIdx) => {
                          const icon = getGameIcon(game.game_type);
                          const borderClass = getGameBorderClass(game.game_type);
                          const headerBadgeClass = getGameBadgeClass(game.game_type);
                          return (
                            <div key={gIdx} className={`border rounded-2xl ${borderClass} overflow-hidden shadow-sm`}>
                              <div className={`px-5 py-3 border-b flex items-center justify-between flex-wrap gap-2 ${headerBadgeClass}`}>
                                <h4 className="font-extrabold text-sm text-slate-800 uppercase tracking-wider flex items-center gap-2">
                                  <span>{icon}</span>
                                  <span>{game.game_name}</span>
                                </h4>
                                <span className="text-[10px] font-extrabold uppercase tracking-widest bg-white/80 border border-slate-200 px-2.5 py-1 rounded-full text-slate-600 shadow-sm">
                                  {game.game_type}
                                </span>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100 text-sm">
                                  <thead className="bg-slate-50 text-[11px] font-bold text-slate-500 uppercase tracking-wider border-b border-slate-100">
                                    <tr>
                                      <th scope="col" className="px-5 py-3 text-left w-16">Rank</th>
                                      <th scope="col" className="px-5 py-3 text-left">Player & District</th>
                                      <th scope="col" className="px-5 py-3 text-right">Standing / Score</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100 bg-white">
                                    {game.results.map((row, rIdx) => {
                                      const scoreBubbleClass = getScoreBubbleClass(game.game_type);
                                      return (
                                        <tr key={rIdx} className="hover:bg-slate-50/50 transition-colors">
                                          <td className="whitespace-nowrap px-5 py-3.5">
                                            {renderRankBadge(row.result_position)}
                                          </td>
                                          <td className="px-5 py-3.5">
                                            <div className="font-bold text-slate-900">{row.player_name || "Participant"}</div>
                                            <div className="text-xs text-slate-500 font-semibold uppercase mt-0.5">{row.district || "Unknown District"}</div>
                                          </td>
                                          <td className="whitespace-nowrap px-5 py-3.5 text-right">
                                            <span className={`inline-flex items-center justify-center font-extrabold text-xs px-3.5 py-1.5 rounded-full tracking-wide shadow-sm border ${scoreBubbleClass}`}>
                                              {formatResultValue(row)}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}



            <PageSectionsRenderer
              pageKey="updates_results"
              className="pt-0"
              excludeKeys={["updates_results_meta", "updates_live_meta"]}
              excludeKeyPrefixes={["updates_result_sheet_"]}
            />
          </div>
        ) : null}
      </main>

      <Footer />
    </div>
  );
};

export default UpdatesResults;

